import { useEffect, useRef, useState } from 'react';

/** Animates a displayed number toward `target` (eased) whenever it changes — for the
 *  satisfying coin tally on the base screen and mission-complete. Pass `initialFrom` to
 *  also animate on first mount (e.g. tally up the coins earned this mission). */
export function useCountUp(target: number, durationMs = 700, initialFrom?: number): number {
  const [display, setDisplay] = useState(initialFrom ?? target);
  const fromRef = useRef(initialFrom ?? target);

  useEffect(() => {
    const from = fromRef.current;
    if (from === target) return;
    let raf = 0;
    let start: number | null = null;
    const step = (ts: number) => {
      if (start === null) start = ts;
      const t = Math.min(1, (ts - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(from + (target - from) * eased));
      if (t < 1) {
        raf = requestAnimationFrame(step);
      } else {
        fromRef.current = target;
      }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);

  return display;
}
